/**
 * Questions about bank lines — the screen for everybody who is asked one.
 *
 * Deliberately not part of Bank statements. The person answering is usually an
 * Employee who has no business seeing the statement, and often has never seen
 * this system before: they get one line, one question, and a box to answer it
 * in. Everything else on this page is there to tell them what they are looking
 * at.
 *
 * It is not an approval screen and does not borrow the words of one. Nobody
 * here authorises anything; the money left the bank days ago.
 *
 * A list first, so a person sees at a glance what is waiting on them, for how
 * much and for how long; the question itself opens beside it. There, what
 * happened comes first, as labelled facts; then the question in somebody's
 * own words; then the one thing this person can do about it; then where the
 * question has been.
 */

import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, formatInr } from '../api'
import { useAuth } from '../auth'
import { ErrorNote, Field, Loading, Stat } from '../components/ui'
import SearchSelect from '../components/SearchSelect'

interface Hop {
  kind: string
  at: string
  by: string | null
  to: string | null
  note: string | null
}

interface Question {
  id: string
  bank_transaction_id: string
  status: string
  note: string
  asked_by: string
  holder_level: string
  holder: string | null
  department: string | null
  location_id: string | null
  category_id: string | null
  answer: string | null
  answered_by: string | null
  answered_at: string | null
  answer_location_id: string | null
  answer_category_id: string | null
  answer_department_id: string | null
  closed_reason: string | null
  hops: Hop[]
  txn_date: string
  amount_paise: number
  direction: string
  narration: string
  bank_account: string
  can_answer: boolean
  can_route: boolean
  can_send_back: boolean
  can_redirect: boolean
  can_apply: boolean
  answer_is_binding: boolean
}

interface Who {
  id: string
  name: string
  role: string
  department: string | null
}

interface Options {
  finance_heads: Who[]
  departments: Who[]
  departments_without_anybody: string[]
  all_departments: Who[]
  people: Who[]
  locations: Who[]
  categories: Who[]
  //: Categories that post to 2530, where the answer also names the director.
  director_categories: string[]
  //: Approved director ledgers, as "2530-001 — Dr. Madhavi Joshi".
  directors: Who[]
  //: Categories that post to 1530 or 4950, where the answer names the investment.
  investment_categories: string[]
  //: Open investment ledgers, as "1530-001 — SBI MF SIP".
  investments: Who[]
}

interface Unplanned {
  bank_transaction_id: string
  txn_date: string
  amount_paise: number
  narration: string
  bank_account: string
  department: string | null
  department_head: string | null
  outstanding: string | null
  accepted_by: string | null
  my_answer_accepts: boolean
  open_question_id: string | null
}

type View = 'mine' | 'others' | 'done' | 'unapproved'
type Sort = 'oldest' | 'newest' | 'largest'

const LEVELS: Record<string, string> = {
  book_keeper: 'the Book Keeper',
  finance_head: 'finance',
  department_head: 'the department',
  employee: 'the person asked',
}

//: Who a question can be passed to, grouped the way people think of them.
//: A Finance Head sees everybody; anybody else sees only Employees.
const ROLE_GROUPS: [string, string][] = [
  ['finance_head', 'Finance Heads'],
  ['book_keeper', 'Book Keepers'],
  ['admin', 'Admins'],
  ['employee', 'Employees'],
]

const HOPS: Record<string, string> = {
  asked: 'Asked',
  routed: 'Passed to',
  sent_back: 'Sent back to',
  answered: 'Answered',
  redirected: 'Moved to',
  closed: 'Closed',
}

function when(at: string): string {
  return new Date(at).toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata',
    dateStyle: 'medium',
    timeStyle: 'short',
  })
}

function day(iso: string) {
  return new Date(iso).toLocaleDateString('en-IN', {
    timeZone: 'Asia/Kolkata',
    day: '2-digit',
    month: 'short',
  })
}

function age(iso: string) {
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000)
  if (days <= 0) return 'today'
  if (days === 1) return '1 day'
  return `${days} days`
}

//: When the question was first asked: the first hop, or failing that the line.
function askedAt(q: Question) {
  return q.hops.find((h) => h.kind === 'asked')?.at ?? q.hops[0]?.at ?? q.txn_date
}

function holderOf(q: Question) {
  return q.holder ?? LEVELS[q.holder_level] ?? 'someone'
}

//: The one phrase that says where a question stands for the viewer.
function standing(q: Question): { label: string; tone: string } {
  if (q.status === 'open' && q.can_answer) return { label: 'Your turn', tone: 'turn' }
  if (q.can_apply) return { label: 'Ready to post', tone: 'turn' }
  if (q.status === 'open') return { label: `With ${holderOf(q)}`, tone: 'advisory' }
  if (q.status === 'answered') return { label: 'Answered', tone: 'pass' }
  return { label: 'Closed', tone: 'closed' }
}

function isMine(q: Question) {
  return (q.status === 'open' && q.can_answer) || q.can_apply
}

//: A part of the open question, under a rule.
function Part({ title, hint, children }: { title: string; hint?: string; children: ReactNode }) {
  return (
    <section>
      <h3>
        {title}
        {hint && <span className="hint"> — {hint}</span>}
      </h3>
      {children}
    </section>
  )
}

function Trail({ hops }: { hops: Hop[] }) {
  return (
    <ol className="trail">
      {hops.map((hop, i) => (
        <li key={i}>
          <div>
            <strong>{HOPS[hop.kind] ?? hop.kind}</strong>
            {hop.to && <> {hop.to}</>}
            {hop.by && <span className="muted"> by {hop.by}</span>}
            <span className="faint"> · {when(hop.at)}</span>
          </div>
          {hop.note && <div className="said">“{hop.note}”</div>}
        </li>
      ))}
    </ol>
  )
}

function Drawer({
  q,
  options,
  onClose,
}: {
  q: Question
  options: Options | undefined
  onClose: () => void
}) {
  const { can } = useAuth()
  const queryClient = useQueryClient()
  //: One field per thing somebody can type. A shared one used to carry a
  //: half-written answer into the reason for moving or sending a question back.
  const [answer, setAnswer] = useState('')
  const [handBack, setHandBack] = useState('')
  const [moveReason, setMoveReason] = useState('')
  const [applyReason, setApplyReason] = useState('')
  const [where, setWhere] = useState<string | undefined>(undefined)
  const [what, setWhat] = useState<string | undefined>(undefined)
  const [dept, setDept] = useState<string | undefined>(undefined)
  const [ledger, setLedger] = useState('')
  const [target, setTarget] = useState('')
  const [stuck, setStuck] = useState(false)
  const stand = standing(q)
  const out = q.direction === 'debit'

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose()
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const act = useMutation({
    mutationFn: (vars: { path: string; body: unknown }) =>
      api.post<Question>(`/banking/questions/${q.id}/${vars.path}`, vars.body),
    onSuccess: () => void queryClient.invalidateQueries(),
  })

  const named = (list: Who[] | undefined, id: string | null) =>
    (list ?? []).find((row) => row.id === id)?.name

  return (
    <>
      <div className="drawer-backdrop" onClick={onClose} />
      <aside className="drawer" role="dialog" aria-modal="true" aria-label="Payment question">
        <header className="drawer-head">
          <div>
            <div className="appr-kind">{out ? 'Payment out' : 'Money in'}</div>
            <h2>{formatInr(q.amount_paise)}</h2>
            <div className="muted">
              {out ? 'Left' : 'Came into'} {q.bank_account} on {q.txn_date}
              {q.department && ` · ${q.department}`}
            </div>
          </div>
          <div className="drawer-amount">
            <span className={`pill ${stand.tone}`}>{stand.label}</span>
            <div className="due">open {age(askedAt(q))}</div>
          </div>
          <button className="quiet close" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </header>

        <div className="drawer-body">
          <ErrorNote error={act.error} />

          <Part title="The payment">
            <dl className="appr-facts">
              <div>
                <dt>Date</dt>
                <dd>{q.txn_date}</dd>
              </div>
              <div>
                <dt>Amount</dt>
                <dd>
                  {formatInr(q.amount_paise)} {out ? 'paid out' : 'received'}
                </dd>
              </div>
              <div>
                <dt>Account</dt>
                <dd>{q.bank_account}</dd>
              </div>
              <div>
                <dt>Department</dt>
                <dd>{q.department ?? 'not known yet'}</dd>
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <dt>On the statement</dt>
                <dd className="mono">{q.narration}</dd>
              </div>
            </dl>
          </Part>

          <Part title="The question">
            <div className="msg ask">
              <div className="who">
                {q.asked_by} asks
                <span className="faint"> · {when(askedAt(q))}</span>
              </div>
              <div>{q.note}</div>
            </div>
            {q.answer && (
              <div className="msg reply">
                <div className="who">
                  {q.answered_by ?? 'Answered'}
                  {q.answered_at && <span className="faint"> · {when(q.answered_at)}</span>}
                </div>
                <div>{q.answer}</div>
              </div>
            )}
            {q.closed_reason && (
              <div className="msg closed">
                <div className="who">Closed</div>
                <div>{q.closed_reason}</div>
              </div>
            )}
            {q.status === 'open' && !q.can_answer && (
              <p className="faint" style={{ margin: '8px 0 0' }}>
                Waiting on {holderOf(q)} to answer.
              </p>
            )}
          </Part>

          {q.can_answer && (
            <Part title="Your answer" hint="this is what the payment gets booked as">
              <Field
                label="What was this payment for?"
                caption="the Book Keeper posts it from what you say here — and on a payment nobody approved first, your answer is what accepts it"
              >
                <textarea
                  rows={3}
                  placeholder="What was bought, and who it was for"
                  value={answer}
                  onChange={(e) => setAnswer(e.target.value)}
                />
              </Field>
              <div className="row">
                <Field label="Which clinic" caption="if you know">
                  <select value={where ?? ''} onChange={(e) => setWhere(e.target.value)}>
                    <option value="">Not sure</option>
                    {(options?.locations ?? []).map((l) => (
                      <option key={l.id} value={l.id}>
                        {l.name}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="Which department" caption="decides who signs it off">
                  <select value={dept ?? ''} onChange={(e) => setDept(e.target.value)}>
                    <option value="">Not sure</option>
                    {(options?.all_departments ?? []).map((d) => (
                      <option key={d.id} value={d.id}>
                        {d.name}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="What kind of spend" caption="if you know">
                  <SearchSelect
                    aria-label="What kind of spend"
                    value={what ?? ''}
                    onChange={setWhat}
                    empty="Not sure"
                    options={(options?.categories ?? []).map((c) => ({
                      value: c.id,
                      label: c.name,
                    }))}
                  />
                </Field>
                {what && (options?.director_categories ?? []).includes(what) && (
                  <Field label="Director ledger" caption="whose loan this is">
                    {(options?.directors ?? []).length === 0 ? (
                      <span className="muted">
                        No director ledger yet: the Book Keeper adds one on Director ledgers, and
                        you approve it.
                      </span>
                    ) : (
                      <SearchSelect
                        aria-label="Director ledger"
                        value={ledger}
                        onChange={setLedger}
                        empty="Not sure"
                        options={(options?.directors ?? []).map((p) => ({
                          value: p.id,
                          label: p.name,
                        }))}
                      />
                    )}
                  </Field>
                )}
                {what && (options?.investment_categories ?? []).includes(what) && (
                  <Field label="Investment ledger" caption="which investment this is">
                    {(options?.investments ?? []).length === 0 ? (
                      <span className="muted">
                        No investment ledger yet: the Book Keeper adds one on Investment ledgers.
                      </span>
                    ) : (
                      <SearchSelect
                        aria-label="Investment ledger"
                        value={ledger}
                        onChange={setLedger}
                        empty="Not sure"
                        options={(options?.investments ?? []).map((p) => ({
                          value: p.id,
                          label: p.name,
                        }))}
                      />
                    )}
                  </Field>
                )}
              </div>
              <div className="actions">
                <button
                  className="primary"
                  disabled={act.isPending || !answer.trim()}
                  onClick={() =>
                    act.mutate({
                      path: 'answer',
                      body: {
                        text: answer,
                        location_id: where || null,
                        category_id: what || null,
                        department_id: dept || null,
                        party_id:
                          what &&
                          [
                            ...(options?.director_categories ?? []),
                            ...(options?.investment_categories ?? []),
                          ].includes(what)
                            ? ledger || null
                            : null,
                      },
                    })
                  }
                >
                  {act.isPending ? 'Saving…' : 'Answer'}
                </button>
                {q.can_send_back && !stuck && (
                  <button className="quiet" onClick={() => setStuck(true)}>
                    I do not know
                  </button>
                )}
              </div>
              {/* Its own box and its own reason: "send it back" must never
                  send the half-written answer as the reason for not answering. */}
              {q.can_send_back && stuck && (
                <div className="inset">
                  <Field
                    label="Send it back to whoever asked"
                    caption="say what you do know, so the next person is not starting over"
                  >
                    <input
                      value={handBack}
                      placeholder="not ours — try Operations, they booked the ambulance"
                      onChange={(e) => setHandBack(e.target.value)}
                    />
                  </Field>
                  <div className="actions">
                    <button
                      disabled={act.isPending || !handBack.trim()}
                      onClick={() => act.mutate({ path: 'send-back', body: { reason: handBack } })}
                    >
                      Send it back
                    </button>
                    <button className="quiet" onClick={() => setStuck(false)}>
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </Part>
          )}

          {q.can_route && (
            <Part title="Pass it on" hint="to somebody who would know">
              <div className="row">
                <Field label="Send to">
                  <select value={target} onChange={(e) => setTarget(e.target.value)}>
                    <option value="">Choose…</option>
                    {(options?.departments ?? []).length > 0 && (
                      <optgroup label="A department (its approval authority)">
                        {(options?.departments ?? []).map((d) => (
                          <option key={d.id} value={`dept:${d.id}`}>
                            {d.name} — {d.department}
                          </option>
                        ))}
                      </optgroup>
                    )}
                    {ROLE_GROUPS.map(([role, label]) => {
                      const people = (options?.people ?? []).filter((p) => p.role === role)
                      return people.length === 0 ? null : (
                        <optgroup key={role} label={label}>
                          {people.map((p) => (
                            <option key={p.id} value={`user:${p.id}`}>
                              {p.name}
                              {p.department ? ` (${p.department})` : ''}
                            </option>
                          ))}
                        </optgroup>
                      )
                    })}
                  </select>
                </Field>
                <div className="shrink">
                  <button
                    disabled={act.isPending || !target}
                    onClick={() =>
                      act.mutate({
                        path: 'route',
                        body: target.startsWith('dept:')
                          ? { department_id: target.slice(5), note: answer || null }
                          : { user_id: target.slice(5), note: answer || null },
                      })
                    }
                  >
                    Send
                  </button>
                </div>
              </div>
              {(options?.departments_without_anybody ?? []).length > 0 && (
                <p className="faint" style={{ marginBottom: 0 }}>
                  Not listed, because no one is the approval authority for them:{' '}
                  {options?.departments_without_anybody.join(', ')}. An Admin sets one under
                  Departments.
                </p>
              )}
            </Part>
          )}

          {q.can_redirect && !q.can_answer && (
            <Part title="It is stuck" hint="finance can take it back">
              <Field label="Why it is being moved" caption="required, and kept">
                <input
                  value={moveReason}
                  placeholder="the person it is with has left"
                  onChange={(e) => setMoveReason(e.target.value)}
                />
              </Field>
              <div className="actions">
                <button
                  disabled={act.isPending || !moveReason.trim()}
                  onClick={() =>
                    act.mutate({ path: 'redirect', body: { user_id: null, reason: moveReason } })
                  }
                >
                  Take it back to me
                </button>
              </div>
            </Part>
          )}

          {q.can_apply && can('banking.question.create') && q.answer_is_binding && (
            <Part title="Put the answer on the bank line" hint="the approver's answer decides it">
              <p className="muted" style={{ marginTop: 0 }}>
                This payment left the bank without an approved request, so what it was is not yours
                to change. It posts as{' '}
                <strong>
                  {named(options?.categories, q.answer_category_id) ?? 'Uncategorised'}
                </strong>
                {q.answer_location_id && (
                  <>
                    {' '}
                    at <strong>{named(options?.locations, q.answer_location_id)}</strong>
                  </>
                )}
                . If that is wrong, ask again and let {q.answered_by} say so.
              </p>
              <div className="actions">
                <button
                  className="primary"
                  disabled={act.isPending}
                  onClick={() =>
                    act.mutate({
                      path: 'apply',
                      body: {
                        location_id: q.answer_location_id,
                        category_id: q.answer_category_id,
                        department_id: q.answer_department_id,
                      },
                    })
                  }
                >
                  Use this on the bank line
                </button>
              </div>
            </Part>
          )}

          {q.can_apply && can('banking.question.create') && !q.answer_is_binding && (
            <Part title="Put the answer on the bank line" hint="then post it as usual">
              <div className="row">
                {/* Falls back to what the line already says, not to nothing:
                    blank would silently erase a clinic somebody had set. */}
                <Field label="Clinic">
                  <select
                    value={where ?? q.answer_location_id ?? q.location_id ?? ''}
                    onChange={(e) => setWhere(e.target.value)}
                  >
                    <option value="">None</option>
                    {(options?.locations ?? []).map((l) => (
                      <option key={l.id} value={l.id}>
                        {l.name}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="Category">
                  <SearchSelect
                    aria-label="Category"
                    value={what ?? q.answer_category_id ?? q.category_id ?? ''}
                    onChange={setWhat}
                    empty="Uncategorised"
                    options={(options?.categories ?? []).map((c) => ({
                      value: c.id,
                      label: c.name,
                    }))}
                  />
                </Field>
              </div>
              <Field
                label="Why, if this differs from the answer"
                caption="required only when you change it"
              >
                <input value={applyReason} onChange={(e) => setApplyReason(e.target.value)} />
              </Field>
              <div className="actions">
                <button
                  className="primary"
                  disabled={act.isPending}
                  onClick={() =>
                    act.mutate({
                      path: 'apply',
                      body: {
                        location_id: (where ?? q.answer_location_id ?? q.location_id) || null,
                        category_id: (what ?? q.answer_category_id ?? q.category_id) || null,
                        department_id: (dept ?? q.answer_department_id) || null,
                        reason: applyReason || null,
                      },
                    })
                  }
                >
                  Use this on the bank line
                </button>
              </div>
            </Part>
          )}

          <Part title="Where it has been">
            <Trail hops={q.hops} />
          </Part>
        </div>
      </aside>
    </>
  )
}

//: Money that left without an approved request, and what each line is
//: waiting on. Nothing to click here: the acceptance happens when a Finance
//: Head or the department head answers the question about the line.
function Unapproved({ rows, onOpen }: { rows: Unplanned[]; onOpen: (id: string) => void }) {
  if (rows.length === 0) return <div className="empty">Every payment had an approval first.</div>
  return (
    <>
      <p className="muted" style={{ margin: 0, padding: '12px 16px' }}>
        These payments were made without a request approved first, so the Book Keeper cannot
        categorise or post them yet. Answering the question about one accepts it — from a Finance
        Head, or from the head of the department the answer names. Accepting is not approving the
        payment: the money has already gone.
      </p>
      <table className="appr-table">
        <thead>
          <tr>
            <th>Date</th>
            <th>Payment</th>
            <th className="num">Amount</th>
            <th>Department</th>
            <th>Where it has got to</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={row.bank_transaction_id}
              className={row.open_question_id ? 'appr-row' : undefined}
              onClick={() => row.open_question_id && onOpen(row.open_question_id)}
            >
              <td className="date">{row.txn_date}</td>
              <td>
                <div className="mono clipped" title={row.narration}>
                  {row.narration}
                </div>
                <div className="faint">{row.bank_account}</div>
              </td>
              <td className="num">
                <strong>{formatInr(row.amount_paise)}</strong>
              </td>
              <td>
                {row.department ?? <span className="faint">not known yet</span>}
                {row.department_head && <div className="faint">{row.department_head}</div>}
              </td>
              <td>
                {row.accepted_by ? (
                  <span className="pill pass">accepted by {row.accepted_by}</span>
                ) : (
                  <span className="muted">{row.outstanding}</span>
                )}
                {row.my_answer_accepts && !row.accepted_by && (
                  <div className="faint">your answer would accept it</div>
                )}
                {row.open_question_id && <div className="faint">open the question →</div>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  )
}

export default function BankQuestions() {
  const [view, setView] = useState<View>('mine')
  const [search, setSearch] = useState('')
  const [sort, setSort] = useState<Sort>('oldest')
  const [openId, setOpenId] = useState<string | null>(null)

  const questions = useQuery({
    queryKey: ['bank-questions', false],
    queryFn: () => api.get<Question[]>('/banking/questions?mine_only=false'),
    staleTime: 0,
  })
  const options = useQuery({
    queryKey: ['bank-question-options'],
    queryFn: () => api.get<Options>('/banking/questions-options'),
  })
  const unplanned = useQuery({
    queryKey: ['unplanned-debits'],
    queryFn: () => api.get<Unplanned[]>('/banking/unplanned'),
    staleTime: 0,
  })

  const all = useMemo(() => questions.data ?? [], [questions.data])
  const mine = all.filter(isMine)
  const others = all.filter((q) => q.status === 'open' && !isMine(q))
  const done = all.filter((q) => q.status !== 'open' && !isMine(q))
  const ready = all.filter((q) => q.can_apply)
  const unapproved = unplanned.data ?? []
  const openOnes = all.filter((q) => q.status === 'open')
  const oldest = openOnes.length
    ? openOnes.reduce((a, b) => (askedAt(a) < askedAt(b) ? a : b))
    : null

  const term = search.trim().toLowerCase()
  const list = view === 'mine' ? mine : view === 'others' ? others : done
  const shown = list
    .filter(
      (q) =>
        !term ||
        [
          q.narration,
          q.note,
          q.asked_by,
          q.holder ?? '',
          q.department ?? '',
          q.answer ?? '',
          q.bank_account,
        ]
          .join(' ')
          .toLowerCase()
          .includes(term),
    )
    .sort((a, b) =>
      sort === 'largest'
        ? b.amount_paise - a.amount_paise
        : sort === 'newest'
          ? askedAt(b).localeCompare(askedAt(a))
          : askedAt(a).localeCompare(askedAt(b)),
    )
  const open = all.find((q) => q.id === openId) ?? null

  if (questions.isLoading) return <Loading what="Loading questions" />

  const tabs: [View, string, number][] = [
    ['mine', 'Waiting on you', mine.length],
    ['others', 'With others', others.length],
    ['done', 'Answered & closed', done.length],
    ['unapproved', 'Paid without approval', unapproved.length],
  ]

  return (
    <>
      <ErrorNote error={questions.error ?? unplanned.error} />

      <div className="grid">
        <Stat
          label="Waiting on you"
          value={String(mine.length - ready.length)}
          note={
            mine.length - ready.length
              ? formatInr(mine.filter((q) => !q.can_apply).reduce((t, q) => t + q.amount_paise, 0))
              : 'nothing to answer'
          }
        />
        <Stat
          label="Ready to post"
          value={String(ready.length)}
          note={
            ready.length ? 'answered — put the answer on the bank line' : 'nothing answered yet'
          }
        />
        <Stat label="With others" value={String(others.length)} note="asked, not yet answered" />
        <Stat
          label="Oldest open question"
          value={oldest ? age(askedAt(oldest)) : '—'}
          note={
            oldest ? `${formatInr(oldest.amount_paise)} · with ${holderOf(oldest)}` : 'none open'
          }
        />
      </div>

      <section className="card">
        <header className="appr-toolbar">
          <div className="seg" role="tablist">
            {tabs.map(([key, label, n]) => (
              <button
                key={key}
                role="tab"
                aria-selected={view === key}
                className={view === key ? 'on' : undefined}
                onClick={() => setView(key)}
              >
                {label}
                <span className="count">{n}</span>
              </button>
            ))}
          </div>
          {view !== 'unapproved' && (
            <div className="appr-filters">
              <input
                type="search"
                placeholder="Search payment, question, person…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                aria-label="Search questions"
              />
              <select
                value={sort}
                onChange={(e) => setSort(e.target.value as Sort)}
                aria-label="Sort"
              >
                <option value="oldest">Oldest first</option>
                <option value="newest">Newest first</option>
                <option value="largest">Largest amount</option>
              </select>
            </div>
          )}
        </header>
        <div className="body tight">
          {view === 'unapproved' ? (
            <Unapproved rows={unapproved} onOpen={setOpenId} />
          ) : shown.length === 0 ? (
            <div className="empty">
              {view === 'mine'
                ? term
                  ? 'Nothing waiting on you matches.'
                  : 'Nothing is waiting on you.'
                : view === 'others'
                  ? 'No question is waiting on anybody else.'
                  : 'No answered or closed questions yet.'}
            </div>
          ) : (
            <table className="appr-table">
              <thead>
                <tr>
                  <th>Payment</th>
                  <th className="num">Amount</th>
                  <th>Question</th>
                  <th>With</th>
                  <th>Open</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((q) => {
                  const stand = standing(q)
                  const out = q.direction === 'debit'
                  return (
                    <tr
                      key={q.id}
                      className={`appr-row${openId === q.id ? ' selected' : ''}`}
                      tabIndex={0}
                      onClick={() => setOpenId(q.id)}
                      onKeyDown={(e) => e.key === 'Enter' && setOpenId(q.id)}
                    >
                      <td>
                        <div className="appr-kind">
                          {out ? 'Paid out' : 'Received'} · {day(q.txn_date)}
                        </div>
                        <div className="mono clipped" title={q.narration}>
                          {q.narration}
                        </div>
                        <div className="faint">{q.bank_account}</div>
                      </td>
                      <td className="num">
                        <strong>{formatInr(q.amount_paise)}</strong>
                      </td>
                      <td>
                        <div className="clipped" title={q.note}>
                          {q.note}
                        </div>
                        <div className="faint">asked by {q.asked_by}</div>
                      </td>
                      <td>
                        {q.status === 'open' ? holderOf(q) : (q.answered_by ?? '—')}
                        {q.department && <div className="faint">{q.department}</div>}
                      </td>
                      <td>
                        {age(askedAt(q))}
                        <div className="faint">since {day(askedAt(q))}</div>
                      </td>
                      <td>
                        <span className={`pill ${stand.tone}`}>{stand.label}</span>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          )}
        </div>
      </section>

      <details className="appr-how">
        <summary>What these questions are</summary>
        <p className="muted">
          A payment left the bank and the Book Keeper cannot tell what it was for. Nothing here is
          an approval — the money has already gone. What is being asked for is the information
          needed to put it in the books correctly: what it was for, which clinic, which department.
          Answer what you know; if it is not yours, say so and send it back, or pass it to someone
          who would know.
        </p>
      </details>

      {open && (
        <Drawer key={open.id} q={open} options={options.data} onClose={() => setOpenId(null)} />
      )}
    </>
  )
}
